<template>
  <div class="vuse-content-wrapper">
    <vuse-section-definition
        :title="$t('Global.Main.companiesManagement')"
        icon="mdi-domain"
    ></vuse-section-definition>
    <v-container fluid class="fill-width">
      <v-sheet class="neu-glow with-radius">
        <v-data-table
            :loading="getLoader"
            :headers="headers"
            :items="companies"
            :single-expand="true"
            :expanded.sync="expanded"
            item-key="companyId"
            sort-by="companyId"
            show-expand
            class="elevation-1 crud-table expanded-crud-table px-6 neu-glow"
        >
          <template v-slot:top>
            <v-toolbar flat color="transparent" class="pt-2">
              <v-spacer/>
              <v-btn color="primary" dark class="mb-2" @click="newCompany">{{
                  $t('Global.Companies.newCompany')
                }}
              </v-btn>
              <company-dialog
                  v-if="dialogActive"
                  :activate="dialogActive"
                  :item="editedItem"
                  @create-item="createItem"
                  @update-item="updateItem"
                  @close-dialog="dialogActive=false"/>
            </v-toolbar>
          </template>

          <template #[`expanded-item`]="{ headers, item }">
            <td :colspan="headers.length" class="neu-glow-inset">
              <company-expansion :item="item" :headers="headers"/>
            </td>
          </template>

          <template #[`item.actions`]="{ item }">
            <v-tooltip content-class='custom-tooltip' left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="primary"
                    size="20"
                    class="mr-3"
                    v-bind="attrs"
                    v-on="on"
                    open-delay="200"
                    @click="editItem(item)"
                >mdi-pencil
                </v-icon>
              </template>
              <span>{{ $t('Global.Nodes.edit') }}</span>
            </v-tooltip>
            <v-tooltip content-class='custom-tooltip' right>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="primary"
                    size="20"
                    v-bind="attrs"
                    v-on="on"
                    open-delay="200"
                    @click="deleteItem(item)"
                >mdi-delete
                </v-icon>
              </template>
              <span>{{ $t('Global.Nodes.delete') }}</span>
            </v-tooltip>
          </template>

        </v-data-table>
      </v-sheet>
    </v-container>
    <confirm-dialog
        ref="confirmDialog"
        :title="confirmDialogTitle"
        :message="confirmDialogMessage"
    />
    <v-snackbar
        v-model="snackbar"
        absolute
        top
        right
        :timeout="snackbarTimeout"
        :color="snackbarColor"
        class="mt-6"
    >
      <span>{{ snackbarMessage }}</span>
      <v-icon class="ml-2" dark>{{ snackbarIcon }}</v-icon>
      <template v-slot:action="{ attrs }">
        <v-btn
            color="white"
            text
            v-bind="attrs"
            @click="snackbar = false"
        >
          {{ $t("Global.ConfirmDialog.buttonTextClose") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import CompanyApi from "@/mixins/Services/CompanyApi";
import ConfirmDialog from "@/components/UI/Dialogs/ConfirmDialog";
import CompanyDialog from './CompanyDialog.vue'
import CompanyExpansion from './CompanyExpansion.vue'
import snackBar from "@/mixins/snackBarMixin"
import loader from "@/mixins/Loader"
import {mapActions} from "vuex";
import Utils from "@/mixins/Utils";
import Logout from "@/mixins/Logout";

export default {
  name: "Companies",
  components: {
    CompanyDialog,
    CompanyExpansion,
    ConfirmDialog
  },
  mixins: [CompanyApi, snackBar, loader, Utils, Logout],
  data() {
    return {
      companies: [],
      expanded: [],
      headers: [
        {text: this.$t('Global.Companies.companyName'), value: "name", align: "left", sortable: true},
        {text: this.$t('Global.Companies.description'), value: "description", align: "left", sortable: true},
        {text: this.$t('Global.Companies.companyId'), value: "companyId", align: "left", sortable: true},
        {text: this.$t('Global.Companies.xsiCompanyId'), value: "xsiCompanyId", align: "left", sortable: true},
        {text: this.$t('Global.Companies.email'), value: "email", align: "left", sortable: true},
        {text: this.$t('Global.Nodes.actions'), value: "actions", align: "left", sortable: false},
        {text: '', value: 'data-table-expand'}
      ],
      editedItem: null,
      dialogActive: false,
      snackbar: false,
      snackbarText: '',
      confirmDialogTitle: "",
      confirmDialogMessage: null
    }
  },

  methods: {
    ...mapActions(["setEditCompanyInfoUser"]),
    async getCompaniesList() {
      try {
        this.setLoader(true)
        await this.setEditCompanyInfoUser()
        this.companies = await this.getCompanies();
        this.setLoader(false)
      } catch (e) {
        this.companies = []
        console.error('[COMPANIES][getCompaniesList] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    newCompany() {
      this.dialogActive = true
      this.editedItem = null
    },
    async createItem(item) {
      this.confirmDialogTitle = this.$t("Global.Companies.newCompany")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        await this.createCompany(item)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Companies.companyCreatedOk"), "success", "4000", "mdi-check-circle")
        await this.getCompaniesList()
      } catch (e) {
        console.error('[COMPANY][createCompany] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    editItem(item) {
      this.dialogActive = true
      this.editedItem = item
    },
    async updateItem(item) {
      this.confirmDialogTitle = this.$t("Global.Companies.editCompany")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        await this.updateCompany(item)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Companies.companyEditedOk"), "success", "4000", "mdi-check-circle")
        await this.getCompaniesList()
      } catch (e) {
        console.error('[COMPANY][updateCompany] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    async deleteItem(item) {
      this.confirmDialogTitle = this.$t("Global.Companies.deleteCompany")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        await this.deleteCompany(item.companyId)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Companies.companyDeletedOk"), "success", "4000", "mdi-check-circle")
        await this.getCompaniesList()
      } catch (e) {
        console.error('[COMPANY][deleteCompany] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
  },
  mounted() {
    this.getCompaniesList()
  },
}
</script>
